// components/SettingsTab.jsx
import React from 'react';

function SettingsTab({
  isHost, isAdmin, users, hostId, admins, userId,
  allowDuplicates, separateEffectDrawing, deleteIfExpired, timer, penaltySettings,
  toggleAllowDuplicates, toggleSeparateEffectDrawing, toggleDeleteIfExpired,
  setGameTimer, kickPlayer, toggleAdmin, updatePenaltySettings, setPenaltySettings
}) {
  return (
    <div className="p-4 bg-surface rounded-xl shadow-lg border border-border">
      <h2 className="text-2xl font-bold mb-6 text-text">Spiel-Einstellungen</h2>

      {/* Host/Admin specific settings */}
      {(isHost || isAdmin) && (
        <div className="space-y-6">
          {/* Allow Duplicates Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-border">
            <span className="text-lg font-semibold text-text-light">Duplikate zulassen</span>
            <label htmlFor="allowDuplicatesToggle" className="flex items-center cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  id="allowDuplicatesToggle"
                  className="sr-only"
                  checked={allowDuplicates}
                  onChange={toggleAllowDuplicates}
                />
                <div className={`block ${allowDuplicates ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'} w-14 h-8 rounded-full transition-colors`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${allowDuplicates ? 'translate-x-full' : ''}`}></div>
              </div>
            </label>
          </div>

          {/* Separate Effect Drawing Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-border">
            <span className="text-lg font-semibold text-text-light">Effekte getrennt ziehen</span>
            <label htmlFor="separateEffectDrawingToggle" className="flex items-center cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  id="separateEffectDrawingToggle"
                  className="sr-only"
                  checked={separateEffectDrawing}
                  onChange={toggleSeparateEffectDrawing}
                />
                <div className={`block ${separateEffectDrawing ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'} w-14 h-8 rounded-full transition-colors`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${separateEffectDrawing ? 'translate-x-full' : ''}`}></div>
              </div>
            </label>
          </div>

          {/* Delete if Expired Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-border">
            <span className="text-lg font-semibold text-text-light">Effekt entfernen wenn abgelaufen</span>
            <label htmlFor="auto-expire" className="flex items-center cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={deleteIfExpired}
                  onChange={toggleDeleteIfExpired}
                  id="auto-expire"
                  className="sr-only"
                />
                <div className={`block ${deleteIfExpired ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'} w-14 h-8 rounded-full transition-colors`}></div>
                <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${deleteIfExpired ? 'translate-x-full' : ''}`}></div>
              </div>
            </label>
          </div>

          {/* User Management */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-border">
            <h3 className="text-lg font-semibold mb-3 text-text-light">Benutzerverwaltung</h3>
            <div className="space-y-3">
              {users.map((u) => (
                <div key={u.id} className="flex justify-between items-center py-2 border-b border-border last:border-b-0">
                  <span className="text-text">{u.name} {u.id === hostId && '(Host)'} {admins.includes(u.id) && u.id !== hostId && '(Admin)'}</span>
                  {(isHost || isAdmin) && u.id !== userId && (
                    <div className="flex gap-2">
                      {isHost && (
                        <button
                          className={`px-3 py-1 rounded-md text-sm font-semibold ${admins.includes(u.id) ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-purple-500 hover:bg-purple-600 text-white'}`}
                          onClick={() => toggleAdmin(u.id, u.name)}
                        >
                          {admins.includes(u.id) ? 'Admin entfernen' : 'Admin setzen'}
                        </button>
                      )}
                      <button
                        className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-md text-sm font-semibold"
                        onClick={() => kickPlayer(u.id)}
                        disabled={u.id === hostId}
                      >
                        Kicken
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Penalty Settings */}
          <div className="space-y-4 mt-6 p-6 rounded-xl shadow-lg border border-border bg-surface">
            <div>
              <label className="block font-medium text-text mb-1">Maximale Penalty-Punkte</label>
              <input
                type="number"
                value={penaltySettings.maxPenaltyPoints}
                onChange={(e) =>
                  setPenaltySettings(prev => ({ ...prev, maxPenaltyPoints: parseInt(e.target.value) }))
                }
                min={0}
                className="w-full border border-border rounded-lg px-4 py-2 bg-background text-text"
              />
            </div>
            <div>
              <label className="block font-medium text-text mb-1">Bestrafung bei Erreichen</label>
              <textarea
                rows={3}
                value={penaltySettings.penaltyDescription}
                onChange={(e) =>
                  setPenaltySettings(prev => ({ ...prev, penaltyDescription: e.target.value }))
                }
                className="w-full border border-border rounded-lg px-4 py-2 bg-background text-text"
                placeholder="Was passiert, wenn ein Spieler die Maximalpunkte erreicht?"
              />
            </div>
            <button
              onClick={() => updatePenaltySettings(penaltySettings.maxPenaltyPoints, penaltySettings.penaltyDescription)}
              className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg shadow"
            >
              Speichern
            </button>
          </div>

          {/* Adjust Timer */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm border border-border">
            <h3 className="text-lg font-semibold mb-3 text-text-light">Timer anpassen</h3>
            <div className="flex gap-3 items-center">
              <input
                type="number"
                value={timer}
                onChange={(e) => setGameTimer(parseInt(e.target.value))}
                className="w-24 shadow appearance-none border rounded py-2 px-3 text-text-light leading-tight focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
              />
              <span className="text-text-light">Sekunden</span>
              <button
                onClick={() => setGameTimer(180)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow-md text-sm"
              >
                Reset auf 3 Min.
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsTab;